/**
 * SessionManager for SUSU Closet platform
 * Handles session storage, authentication state, and backend data synchronization
 * Updated to work with RTK Query and backend APIs
 */
class SessionManager {
  constructor() {
    this.storagePrefix = 'susu_auth_';
    this.sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours
    this.store = null; // Will be set by Redux store
  }

  /**
   * Set Redux store reference for backend data operations
   * @param {Object} store - Redux store instance
   */
  setStore(store) {
    this.store = store;
  }

  /**
   * Store session data received from SHEYOU authentication
   * @param {Object} sessionData - Session data including token, user
   */
  storeSession(sessionData) {
    try {
      const session = {
        ...sessionData,
        timestamp: Date.now(),
        platform: 'susu_closet',
        source: 'sheyou'
      };

      localStorage.setItem(`${this.storagePrefix}session`, JSON.stringify(session));
      localStorage.setItem(`${this.storagePrefix}token`, sessionData.token);
      localStorage.setItem(`${this.storagePrefix}user`, JSON.stringify(sessionData.user));

      // Trigger backend data loading after successful authentication
      this.loadUserDataFromBackend();

    } catch (error) {
      console.error('Failed to store session:', error);
    }
  }

  /**
   * Get current session data
   * @returns {Object|null} Session data or null if not found/expired
   */
  getSession() {
    try {
      const sessionData = localStorage.getItem(`${this.storagePrefix}session`);

      if (!sessionData) {
        return null;
      }

      const session = JSON.parse(sessionData);

      // Check if session is expired
      if (this.isSessionExpired(session)) {
        this.clearSession();
        return null;
      }

      return session;

    } catch (error) {
      console.error('Failed to get session:', error);
      return null;
    }
  }

  /**
   * Get stored authentication token
   * @returns {string|null} Token or null if not found
   */
  getToken() {
    return localStorage.getItem(`${this.storagePrefix}token`);
  }

  /**
   * Get stored user data
   * @returns {Object|null} User data or null if not found
   */
  getUser() {
    try {
      const userData = localStorage.getItem(`${this.storagePrefix}user`);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Failed to get user data:', error);
      return null;
    }
  }

  /**
   * Check if session is expired
   * @param {Object} session - Session object to check
   * @returns {boolean} True if expired
   */
  isSessionExpired(session) {
    if (!session || !session.timestamp) {
      return true;
    }

    const age = Date.now() - session.timestamp;
    return age > this.sessionTimeout;
  }

  /**
   * Validate current session
   * @returns {boolean} True if session is valid and not expired
   */
  isSessionValid() {
    const session = this.getSession();
    return session !== null && !this.isSessionExpired(session);
  }

  /**
   * Clear all session data and cleanup backend cache
   */
  clearSession() {
    try {
      localStorage.removeItem(`${this.storagePrefix}session`);
      localStorage.removeItem(`${this.storagePrefix}token`);
      localStorage.removeItem(`${this.storagePrefix}user`);

      // Clear any temporary auth data
      sessionStorage.removeItem('auth_return_url');
      sessionStorage.removeItem('auth_platform');

      // Clear RTK Query cache but preserve backend data
      this.clearRTKQueryCache();

      // Clear VTO storage but preserve URLs for offline access
      this.clearVTOStorageOnLogout();

    } catch (error) {
      console.error('Failed to clear session:', error);
    }
  }

  /**
   * Refresh session timestamp to extend expiration
   */
  refreshSession() {
    const session = this.getSession();

    if (session) {
      session.timestamp = Date.now();
      localStorage.setItem(`${this.storagePrefix}session`, JSON.stringify(session));
    }
  }

  /**
   * Check if user has access to SUSU Closet platform
   * @returns {boolean} True if user has access
   */
  hasPlatformAccess() {
    const user = this.getUser();

    // All authenticated users have access to SUSU Closet platform
    return user !== null;
  }

  /**
   * Handle authentication callback from SHEYOU
   * @param {string} token - JWT token from SHEYOU
   * @param {Object} user - User data from SHEYOU
   * @returns {boolean} True if authentication was successful
   */
  handleAuthCallback(token, user) {
    try {
      // All authenticated users have access to SUSU Closet platform
      console.log('Storing session for user:', user.email);

      // Store session data (this will trigger backend data loading)
      this.storeSession({ token, user });

      return true;

    } catch (error) {
      console.error('Failed to handle auth callback:', error);
      return false;
    }
  }

  /**
   * Get session statistics for debugging
   * @returns {Object} Session statistics
   */
  getSessionStats() {
    const session = this.getSession();

    if (!session) {
      return {
        hasSession: false,
        isExpired: true,
        age: 0,
        timeRemaining: 0
      };
    }

    const age = Date.now() - session.timestamp;
    const timeRemaining = Math.max(0, this.sessionTimeout - age);

    return {
      hasSession: true,
      isExpired: this.isSessionExpired(session),
      age,
      timeRemaining,
      platform: session.platform,
      source: session.source,
      userId: session.user?.id,
      hasAccess: this.hasPlatformAccess()
    };
  }

  /**
   * Load user data from backend after authentication
   * Uses RTK Query to fetch and cache user's data
   */
  async loadUserDataFromBackend() {
    if (!this.store) {
      console.warn('Redux store not set, cannot load backend data');
      return;
    }

    try {
      console.log('[Session] Loading user data from backend...');

      // Trigger RTK Query data fetching for all user data
      const promises = [
        // Load user's clothing items
        this.store.dispatch({
          type: 'closetApi/endpoints/getUserClothing/initiate',
          payload: { limit: 100, sort: 'createdAt:desc' }
        }),

        // Load user's person images
        this.store.dispatch({
          type: 'personImageApi/endpoints/getUserPersonImages/initiate',
          payload: { limit: 20, sort: 'createdAt:desc' }
        }),

        // Load user's background images
        this.store.dispatch({
          type: 'backgroundImageApi/endpoints/getUserBackgrounds/initiate',
          payload: { limit: 50, sort: 'createdAt:desc' }
        }),

        // Load user's VTO results
        this.store.dispatch({
          type: 'vtoResultApi/endpoints/getUserVTOResults/initiate',
          payload: { limit: 50, sort: 'createdAt:desc' }
        })
      ];

      await Promise.allSettled(promises);
      console.log('[Session] User data loaded from backend');

      // Restore session state from backend data
      this.restoreSessionFromBackend();

    } catch (error) {
      console.error('[Session] Failed to load user data from backend:', error);
    }
  }

  /**
   * Restore session state from backend data
   * Synchronizes localStorage with backend data
   */
  restoreSessionFromBackend() {
    if (!this.store) return;

    try {
      const state = this.store.getState();

      // Get person images data
      const personImages = state.personImageApi?.queries;
      if (personImages) {
        const personImagesQuery = Object.values(personImages).find(
          query => query?.endpointName === 'getUserPersonImages' && query?.data?.length > 0
        );

        if (personImagesQuery?.data?.length > 0 && !state.personImage?.image) {
          const images = personImagesQuery.data;

          // First, try to find default image
          const defaultImage = images.find(img => img.isDefault);

          if (defaultImage) {
            console.log('[Session] Restoring default person image from backend:', defaultImage.id);
            this.store.dispatch({
              type: 'personImage/setActivePersonImage',
              payload: {
                personImageId: defaultImage.id,
                imageUrl: defaultImage.removedBgImage?.url || defaultImage.image?.url,
                imageDimensions: {
                  width: defaultImage.image?.width || 400,
                  height: defaultImage.image?.height || 600
                }
              }
            });
            this.store.dispatch({
              type: 'personImage/setCanvasIntegrationMode',
              payload: true
            });
          } else {
            // Fallback: load latest image if no default is set
            const latestImage = images[0]; // Already sorted by created_at:desc
            console.log('[Session] No default image found, restoring latest person image from backend:', latestImage.id);
            this.store.dispatch({
              type: 'personImage/setActivePersonImage',
              payload: {
                personImageId: latestImage.id,
                imageUrl: latestImage.removedBgImage?.url || latestImage.image?.url,
                imageDimensions: {
                  width: latestImage.image?.width || 400,
                  height: latestImage.image?.height || 600
                }
              }
            });
            this.store.dispatch({
              type: 'personImage/setCanvasIntegrationMode',
              payload: true
            });
          }
        }
      }

      // Restore VTO state if available
      const vtoResults = state.vtoResultApi?.queries;
      if (vtoResults) {
        const latestVTOQuery = Object.values(vtoResults).find(
          query => query?.endpointName === 'getUserVTOResults' && query?.data?.results?.length > 0
        );

        if (latestVTOQuery?.data?.results?.[0]) {
          console.log('[Session] Restoring latest VTO result from backend');
          const latestVTO = latestVTOQuery.data.results[0];

          // Update VTO history in localStorage with backend data
          const { VTOStorage } = require('../utils/vtoStorage');
          VTOStorage.saveHistory(latestVTOQuery.data.results);
        }
      }

    } catch (error) {
      console.error('[Session] Failed to restore session from backend:', error);
    }
  }

  /**
   * Clear RTK Query cache on logout while preserving backend data
   */
  clearRTKQueryCache() {
    if (!this.store) return;

    try {
      // Reset RTK Query cache
      this.store.dispatch({ type: 'api/util/resetApiState' });
      console.log('[Session] Cleared RTK Query cache');
    } catch (error) {
      console.warn('Failed to clear RTK Query cache:', error);
    }
  }

  /**
   * Clear VTO storage on logout but preserve URLs for offline access
   */
  clearVTOStorageOnLogout() {
    try {
      const { VTOStorage } = require('../utils/vtoStorage');

      // Get current data before clearing
      const enhancedState = VTOStorage.loadEnhancedState();
      const history = VTOStorage.loadHistory();

      // Clear all VTO data
      VTOStorage.clearAllVTOData();

      // Preserve a minimal set of URLs for offline access (last 5 items)
      if (history && history.length > 0) {
        const minimalHistory = history.slice(0, 5).map(item => ({
          id: item.id,
          imageUrl: item.imageUrl,
          thumbnailUrl: item.thumbnailUrl,
          timestamp: item.timestamp,
          garmentType: item.garmentType,
          offline: true // Mark as offline-only data
        }));

        VTOStorage.saveHistory(minimalHistory);
        console.log('[Session] Preserved minimal VTO history for offline access');
      }

    } catch (error) {
      console.warn('Failed to clear VTO storage on logout:', error);
    }
  }

  /**
   * Handle user switching - clear current user data and load new user data
   * @param {string} newToken - New user's token
   * @param {Object} newUser - New user data
   */
  async switchUser(newToken, newUser) {
    try {
      console.log('[Session] Switching user from', this.getUser()?.email, 'to', newUser.email);

      // Clear current session and cache
      this.clearSession();

      // Store new session
      this.storeSession({ token: newToken, user: newUser });

      // Load new user's data
      await this.loadUserDataFromBackend();

      console.log('[Session] User switch completed');
      return true;

    } catch (error) {
      console.error('[Session] Failed to switch user:', error);
      return false;
    }
  }

  /**
   * Sync session with SHEYOU platform (check for updates)
   * This can be called periodically to ensure session is still valid
   * @returns {Promise<boolean>} True if session is still valid
   */
  async syncWithSHEYOU() {
    const token = this.getToken();

    if (!token) {
      return false;
    }

    try {
      const isDev = window.location.hostname === 'localhost';
      let backendUrl = isDev
        ? process.env.REACT_APP_backend_baseUrl_localTest
        : process.env.REACT_APP_backend_baseUrl_live;

      if (!backendUrl) backendUrl = 'http://localhost:1337';
      
      if (backendUrl.includes(':1337') && !backendUrl.endsWith('/api')) {
        backendUrl = `${backendUrl}/api`;
      }

      const response = await fetch(`${backendUrl}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token, platform: 'susu_closet' })
      });

      if (!response.ok) {
        // Token is invalid, clear session
        this.clearSession();
        return false;
      }

      const result = await response.json();

      if (!result.valid) {
        this.clearSession();
        return false;
      }

      // Update session with fresh data
      this.refreshSession();

      // Optionally refresh backend data if needed
      if (result.dataChanged) {
        await this.loadUserDataFromBackend();
      }

      return true;

    } catch (error) {
      console.error('Failed to sync with SHEYOU:', error);
      return false;
    }
  }

  /**
   * Get fallback data when backend is unavailable
   * @returns {Object} Offline data from localStorage
   */
  getOfflineData() {
    try {
      const { VTOStorage } = require('../utils/vtoStorage');

      return {
        vtoHistory: VTOStorage.loadHistory().filter(item => item.offline),
        wearerAndWearing: VTOStorage.getWearerAndWearing(),
        hasOfflineData: true
      };

    } catch (error) {
      console.warn('Failed to get offline data:', error);
      return {
        vtoHistory: [],
        wearerAndWearing: { wearer: {}, wearing: {} },
        hasOfflineData: false
      };
    }
  }
}

export default SessionManager;